import { useMemo, useState } from 'react';
import type { MetricSnapshot } from '@gamolution/shared';

const BAR_SAMPLES = 44;

export interface ModelBenchTimelineProps {
  tick: number;
  playing: boolean;
  speed: number;
  ticking: boolean;
  metricsHistory: readonly MetricSnapshot[];
  series?: readonly number[] | undefined;
  rateUnit?: string | undefined;
  tickUnit?: string | undefined;
  maxTick?: number | undefined;
  onScrub?: ((tick: number) => void) | undefined;
  hintLines?: readonly [string, string] | undefined;
  onPlayPause: () => void;
  onStep: () => void;
  onSpeedChange: (speed: number) => void;
}

export function ModelBenchTimeline({
  tick,
  playing,
  speed,
  ticking,
  metricsHistory,
  series,
  rateUnit = 'TICKS',
  tickUnit = 'TICK',
  maxTick,
  onScrub,
  hintLines = ['SCRUB TO REPLAY', 'DRAG UP TO FORK'],
  onPlayPause,
  onSpeedChange,
}: ModelBenchTimelineProps) {
  const [speedOpen, setSpeedOpen] = useState(false);
  const replayProgress = maxTick === undefined
    ? null
    : ((Math.max(1, tick) - 1) / Math.max(1, maxTick - 1)) * 100;
  const popBars = useMemo(() => {
    const vals = series === undefined
      ? metricsHistory.map((m) => m.population.alive)
      : [...series];
    const slice = vals.length > BAR_SAMPLES ? vals.slice(-BAR_SAMPLES) : vals;
    if (slice.length === 0) return [{ h: 14 }];
    if (series !== undefined) {
      return slice.map((value) => ({ h: Math.max(6, Math.round(value * 100)) }));
    }
    const max = Math.max(...slice, 1);
    return slice.map((v) => ({ h: Math.round((v / max) * 88) + 6 }));
  }, [metricsHistory, series]);

  const rateLabel = playing ? `${speed}/s\n${rateUnit}` : 'PAUSED';

  return (
    <footer className="cn-timeline">
      <button
        type="button"
        className="cn-timeline__play"
        onClick={onPlayPause}
        disabled={ticking}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? '❚❚' : '▶'}
      </button>
      <button
        type="button"
        className="cn-timeline__rate-btn"
        onClick={() => setSpeedOpen((o) => !o)}
        aria-label="Tick rate"
      >
        {rateLabel.split('\n').map((line, i) => (
          <span key={i}>{line}</span>
        ))}
      </button>
      {speedOpen && (
        <div className="cn-timeline__speed-pop">
          <input
            type="range"
            min={1}
            max={20}
            value={speed}
            onChange={(e) => onSpeedChange(Number(e.target.value))}
          />
          <span>{speed}x</span>
        </div>
      )}
      <div className="cn-timeline__bars-wrap">
        {maxTick !== undefined && onScrub !== undefined && (
          <>
            <input
              className="cn-timeline__scrubber"
              type="range"
              min={1}
              max={maxTick}
              value={Math.min(maxTick, Math.max(1, tick))}
              aria-label="Replay timeline"
              onChange={(event) => onScrub(Number(event.target.value))}
            />
          </>
        )}
        <div className="cn-timeline__bars">
          {popBars.map((b, i) => (
            <div key={i} className="cn-timeline__bar" style={{ height: `${b.h}%` }} />
          ))}
        </div>
        <div
          className="cn-timeline__now-line"
          style={replayProgress === null ? undefined : { left: `${replayProgress}%`, right: 'auto' }}
        />
        <span
          className="cn-timeline__now"
          style={replayProgress === null ? undefined : { left: `clamp(0px, calc(${replayProgress}% - 28px), calc(100% - 28px))`, right: 'auto' }}
        >NOW</span>
      </div>
      <div className="cn-timeline__hints">
        {hintLines[0]}
        <br />
        {hintLines[1]}
      </div>
      <div className="cn-timeline__tick-block">
        <span className="cn-timeline__tick">{tick}</span>
        <span className="cn-timeline__tick-label">{tickUnit}</span>
      </div>
    </footer>
  );
}
